// frontend/src/components/remediation/RemediationOperationsPanel.jsx
import React, { useCallback, useEffect, useMemo, useRef, useState } from "react";
import { Button } from "../../design-system/components";
import { MetricCard } from "./SharedRemediationUI";
import {
  cleanValue,
  formatTimestamp,
  getTaskStatusLabel,
  normalizeText,
  safeNumber,
} from "../../utils/complianceFormatters";
import {
  bulkAssignRemediationTasks,
  bulkUpdateRemediationStatus,
  fetchRemediationOperationsAnalytics,
  fetchRemediationOperationsQueue,
} from "../../services/remediationOperationsApi";

const BULK_NOTICE_AUTO_CLEAR_MS = 3000;
const OPERATIONS_QUEUE_PAGE_SIZE = 10;

const QUEUE_TABS = [
  { key: "all", label: "All Active", params: {} },
  { key: "open", label: "Open", params: { queue: "open" } },
  { key: "in_progress", label: "In Progress", params: { queue: "in_progress" } },
  { key: "validation_failed", label: "Validation Failed", params: { queue: "validation_failed" } },
  { key: "ready_for_validation", label: "Ready", params: { queue: "ready_for_validation" } },
  {
    key: "approved_exception",
    label: "Approved Exceptions",
    params: {
      includeClosed: true,
      validation_status: "approved_exception",
      validationStatus: "approved_exception",
    },
  },
  { key: "closed", label: "Closed", params: { includeClosed: true, queue: "closed", status: "closed" } },
  { key: "overdue", label: "Overdue", params: { overdue: true } },
];


const BULK_STATUS_OPTIONS = [
  { value: "open", label: "Open" },
  { value: "assigned", label: "Assigned" },
  { value: "in_progress", label: "In Progress" },
  { value: "blocked", label: "Blocked" },
  { value: "ready_for_validation", label: "Ready for Validation" },
  { value: "deferred", label: "Deferred" },
];

function getQueueTasks(payload) {
  if (Array.isArray(payload?.tasks)) return payload.tasks;
  if (Array.isArray(payload?.items)) return payload.items;
  if (Array.isArray(payload)) return payload;
  return [];
}

function mergeQueuePayload(currentPayload, nextPayload) {
  const currentTasks = getQueueTasks(currentPayload);
  const nextTasks = getQueueTasks(nextPayload);
  const mergedTasks = [];
  const seen = new Set();

  [...currentTasks, ...nextTasks].forEach((task) => {
    const key = task?.id || [task?.framework, task?.control_id, task?.rule_id, task?.resource_id].filter(Boolean).join("::");
    if (!key || seen.has(key)) return;
    seen.add(key);
    mergedTasks.push(task);
  });

  return {
    ...(nextPayload || {}),
    tasks: mergedTasks,
    returned: mergedTasks.length,
  };
}

function getDistributionTotal(items = [], key) {
  const match = (Array.isArray(items) ? items : []).find(
    (item) => normalizeText(item?.key) === normalizeText(key)
  );
  return safeNumber(match?.total, 0);
}
function formatTitleCase(value = "") {
  const cleaned = cleanValue(value, "").replace(/_/g, " ").trim();
  if (!cleaned) return "";

  return cleaned
    .split(/\s+/)
    .map((word) => {
      if (/^[A-Z0-9]{2,}$/.test(word)) return word;
      return word.charAt(0).toUpperCase() + word.slice(1).toLowerCase();
    })
    .join(" ");
}

function formatValidationLabel(value) {
  const normalized = normalizeText(value || "pending");
  if (normalized === "approved_exception") return "Approved Exception";
  if (normalized === "manual_override") return "Manual Override";
  if (normalized === "ready_for_validation") return "Ready For Validation";
  if (normalized === "passed" || normalized === "pass") return "Passed";
  if (normalized === "failed" || normalized === "fail") return "Failed";
  if (normalized === "stale") return "Stale";
  if (normalized === "pending") return "Pending";
  return formatTitleCase(value || "pending") || "Pending";
}

function isApprovedExceptionTask(task = {}) {
  const validationStatus = normalizeText(task.validation_status || task.validationStatus || "");
  const metadata = task.task_metadata || task.metadata || {};
  const lastValidation = metadata.last_validation || metadata.lastValidation || {};
  const lastStatus = normalizeText(
    lastValidation.status ||
      lastValidation.validation_status ||
      lastValidation.result ||
      lastValidation.validation_result ||
      ""
  );
  const lastMetadata = lastValidation.metadata || {};
  const governance = normalizeText(
    lastMetadata.validation_governance || lastMetadata.governance || lastMetadata.exception_type || ""
  );
  const decisionPath = normalizeText(lastMetadata.decision_path || "");

  return (
    validationStatus === "approved_exception" ||
    lastStatus === "approved_exception" ||
    governance === "approved_exception" ||
    decisionPath.includes("approved_exception") ||
    decisionPath.includes("retain_as_approved_exception")
  );
}

function isClosedQueueTask(task = {}) {
  const status = normalizeText(task.status || task.task_status || "");
  return status === "closed" || status === "validated";
}

function getApprovedExceptionTotal(analytics = {}, tasks = []) {
  const direct = safeNumber(
    analytics?.approved_exception_tasks ??
      analytics?.approved_exceptions ??
      analytics?.accepted_risk_tasks ??
      analytics?.accepted_risks,
    null
  );

  if (direct !== null && Number.isFinite(direct)) return direct;

  const validationDistribution = Array.isArray(analytics?.validation_distribution)
    ? analytics.validation_distribution
    : Array.isArray(analytics?.validation_status_distribution)
    ? analytics.validation_status_distribution
    : [];
  const fromDistribution = getDistributionTotal(validationDistribution, "approved_exception");
  if (fromDistribution > 0) return fromDistribution;

  return (Array.isArray(tasks) ? tasks : []).filter(isApprovedExceptionTask).length;
}


function statusTone(status, validationStatus) {
  const cleanStatus = normalizeText(status);
  const cleanValidation = normalizeText(validationStatus);

  if (cleanValidation === "failed") return "danger";
  if (cleanStatus === "blocked") return "warn";
  if (cleanStatus === "closed" || cleanStatus === "validated" || cleanValidation === "passed" || cleanValidation === "approved_exception") return "ok";
  if (cleanStatus === "ready_for_validation") return "accent";
  if (cleanStatus === "in_progress") return "warn";
  return "default";
}

function chipStyle(tone = "default") {
  const color =
    tone === "danger"
      ? "var(--danger)"
      : tone === "warn"
      ? "var(--warn)"
      : tone === "ok"
      ? "var(--ok)"
      : tone === "accent"
      ? "var(--accent)"
      : "var(--muted)";

  return {
    display: "inline-flex",
    alignItems: "center",
    borderRadius: 999,
    padding: "4px 8px",
    fontSize: 11,
    fontWeight: 850,
    lineHeight: 1.1,
    color,
    border: `1px solid color-mix(in srgb, ${color}, transparent 62%)`,
    background: `color-mix(in srgb, ${color}, transparent 90%)`,
    whiteSpace: "nowrap",
  };
}

function compactDate(value) {
  if (!value) return "—";
  const dateValue = new Date(value);
  if (Number.isNaN(dateValue.getTime())) return String(value);
  return dateValue.toLocaleDateString(undefined, {
    month: "short",
    day: "numeric",
    year: "numeric",
  });
}

function formatSlaStateLabel(value) {
  const normalized = normalizeText(value);

  if (normalized === "on_track") return "On track";
  if (normalized === "due_soon") return "Due soon";
  if (normalized === "overdue") return "Overdue";
  if (normalized === "no_deadline") return "No deadline";
  if (normalized === "closed") return "Closed";

  return cleanValue(value, "Unknown").replace(/_/g, " ");
}

function isTaskOverdue(task = {}) {
  const state = normalizeText(task.sla_state);
  if (task.is_overdue || task.isOverdue) return true;
  if (state === "overdue") return true;

  const dueDate = task.due_date || task.dueDate || task.deadline_at || task.deadlineAt;
  if (!dueDate) return false;

  const dueMs = new Date(dueDate).getTime();
  return Number.isFinite(dueMs) && dueMs < Date.now();
}

function getSlaTone(task = {}) {
  const state = normalizeText(task.sla_state);

  if (task.is_overdue || state === "overdue") return "danger";
  if (state === "due_soon") return "warn";
  if (state === "on_track" || state === "closed") return "ok";
  if (state === "no_deadline") return "default";

  return "warn";
}

function getDeadlineLabels(task = {}) {
  const rawDeadline = task.due_date || task.deadline_at;
  const dateLabel = rawDeadline ? compactDate(rawDeadline) : "No deadline";

  if (!rawDeadline) {
    return {
      dateLabel,
      helperLabel: "No SLA deadline",
      tone: "default",
    };
  }

  const daysUntilDue = safeNumber(task.days_until_due, null);
  const overdue = Boolean(task.is_overdue) || normalizeText(task.sla_state) === "overdue";

  if (overdue) {
    const overdueDays = Number.isFinite(daysUntilDue) ? Math.abs(Math.min(daysUntilDue, 0)) : null;
    return {
      dateLabel,
      helperLabel:
        overdueDays === null
          ? "Overdue"
          : `Overdue by ${overdueDays} day${overdueDays === 1 ? "" : "s"}`,
      tone: "danger",
    };
  }

  if (daysUntilDue === 0) {
    return { dateLabel, helperLabel: "Due today", tone: "warn" };
  }

  if (daysUntilDue === 1) {
    return { dateLabel, helperLabel: "Due tomorrow", tone: "warn" };
  }

  if (Number.isFinite(daysUntilDue)) {
    return {
      dateLabel,
      helperLabel: `Due in ${daysUntilDue} days`,
      tone: daysUntilDue <= 3 ? "warn" : "default",
    };
  }

  return {
    dateLabel,
    helperLabel: cleanValue(task.deadline_source, "Deadline set"),
    tone: "default",
  };
}

function getClosedTimestampValue(task = {}) {
  return (
    task.closed_at ||
    task.closedAt ||
    task.closed_on ||
    task.closedOn ||
    task.updated_at ||
    task.updatedAt ||
    null
  );
}

function timestampMs(value) {
  if (!value) return 0;
  const parsed = new Date(value).getTime();
  return Number.isFinite(parsed) ? parsed : 0;
}

function formatClosedAgo(value) {
  const parsed = timestampMs(value);
  if (!parsed) return "Closed date unavailable";

  const diffMs = Date.now() - parsed;
  const diffMinutes = Math.max(0, Math.round(diffMs / 60000));

  if (diffMinutes < 1) return "Closed just now";
  if (diffMinutes < 60) return `Closed ${diffMinutes} min ago`;

  const diffHours = Math.round(diffMinutes / 60);
  if (diffHours < 24) return `Closed ${diffHours} hr${diffHours === 1 ? "" : "s"} ago`;

  const diffDays = Math.round(diffHours / 24);
  if (diffDays < 45) return `Closed ${diffDays} day${diffDays === 1 ? "" : "s"} ago`;

  return "Closed";
}

function getClosedDateLabels(task = {}) {
  const closedAt = getClosedTimestampValue(task);

  if (!closedAt) {
    return {
      dateLabel: "Closed date unknown",
      helperLabel: "Review audit trail",
      tone: "default",
    };
  }

  return {
    dateLabel: compactDate(closedAt),
    helperLabel: formatClosedAgo(closedAt),
    tone: "ok",
  };
}

function sortClosedTasksMostRecentFirst(tasks = []) {
  return [...(Array.isArray(tasks) ? tasks : [])].sort((left, right) => {
    const rightClosed = timestampMs(getClosedTimestampValue(right));
    const leftClosed = timestampMs(getClosedTimestampValue(left));

    if (rightClosed !== leftClosed) return rightClosed - leftClosed;

    const rightCreated = timestampMs(right?.created_at || right?.createdAt);
    const leftCreated = timestampMs(left?.created_at || left?.createdAt);
    return rightCreated - leftCreated;
  });
}

function formatSlaWindowLabel(task = {}) {
  const slaHours = safeNumber(task.sla_hours, null);
  const state = normalizeText(task.sla_state);
  const deadlineSource = normalizeText(task.deadline_source);

  if (state === "no_deadline" || state === "unknown") return "No SLA";

  if (deadlineSource === "task_due_date") return "Manual due date";
  if (deadlineSource === "report_due_date") return "Report deadline";
  if (deadlineSource === "sop_due_date") return "SOP deadline";

  if (slaHours === null || !Number.isFinite(slaHours) || slaHours <= 0) {
    return cleanValue(task.deadline_source, "No SLA").replace(/_/g, " ");
  }

  if (deadlineSource === "control_sla" || deadlineSource === "") {
    if (slaHours % 24 === 0) {
      const days = slaHours / 24;
      return `${days}-day SLA`;
    }

    return `${slaHours}-hour SLA`;
  }

  return cleanValue(task.deadline_source, "SLA").replace(/_/g, " ");
}

function taskLabel(task = {}) {
  const control = cleanValue(task.control_id, "—");
  const rule = cleanValue(task.rule_id, "");
  return rule ? `${control} / ${rule}` : control;
}

function getPrimaryWorkflowAction(task = {}) {
  const status = normalizeText(task.status);
  const validationStatus = normalizeText(task.validation_status);

  if (status === "closed") return null;

  if (validationStatus === "approved_exception") {
    return null;
  }

  if (status === "ready_for_validation") {
    return { key: "run_validation", label: "Run Validation", tone: "primary" };
  }

  if (status === "in_progress") {
    return { key: "mark_ready", label: "Ready for Validation", tone: "primary" };
  }

  if (["open", "assigned", "deferred"].includes(status)) {
    return { key: "start_work", label: "Start Work", tone: "primary" };
  }

  if (validationStatus === "failed") {
    return { key: "fix_plan", label: "Fix Plan", tone: "ghost" };
  }

  return null;
}

function inputStyle(extra = {}) {
  return {
    width: "100%",
    border: "1px solid color-mix(in srgb, var(--text), transparent 84%)",
    background: "color-mix(in srgb, var(--panel-2), transparent 10%)",
    color: "var(--text)",
    borderRadius: 12,
    padding: "8px 10px",
    fontSize: 12,
    fontWeight: 700,
    outline: "none",
    ...extra,
  };
}

function labelStyle() {
  return {
    display: "grid",
    gap: 5,
    color: "var(--muted)",
    fontSize: 11,
    fontWeight: 850,
    textTransform: "uppercase",
    letterSpacing: ".05em",
  };
}

function statusDropdownButtonStyle(isOpen) {
  return {
    ...inputStyle({
      display: "flex",
      alignItems: "center",
      justifyContent: "space-between",
      gap: 10,
      cursor: "pointer",
      textAlign: "left",
      minHeight: 38,
      borderColor: isOpen
        ? "color-mix(in srgb, var(--accent), transparent 38%)"
        : "color-mix(in srgb, var(--text), transparent 84%)",
      boxShadow: isOpen
        ? "0 0 0 3px color-mix(in srgb, var(--accent), transparent 78%)"
        : "none",
    }),
  };
}

function StatusDropdown({ value, onChange, disabled = false }) {
  const [open, setOpen] = useState(false);
  const rootRef = useRef(null);

  const selectedOption =
    BULK_STATUS_OPTIONS.find((option) => option.value === value) ||
    BULK_STATUS_OPTIONS[0];

  useEffect(() => {
    if (!open) return undefined;

    const handlePointerDown = (event) => {
      if (!rootRef.current?.contains(event.target)) {
        setOpen(false);
      }
    };

    const handleKeyDown = (event) => {
      if (event.key === "Escape") setOpen(false);
    };

    document.addEventListener("mousedown", handlePointerDown);
    document.addEventListener("keydown", handleKeyDown);

    return () => {
      document.removeEventListener("mousedown", handlePointerDown);
      document.removeEventListener("keydown", handleKeyDown);
    };
  }, [open]);

  return (
    <div
      ref={rootRef}
      style={{
        position: "relative",
        zIndex: open ? 9998 : 1,
      }}
    >
      <button
        type="button"
        disabled={disabled}
        onClick={() => setOpen((current) => !current)}
        style={statusDropdownButtonStyle(open)}
        aria-haspopup="listbox"
        aria-expanded={open}
      >
        <span>{selectedOption.label}</span>
        <span
          style={{
            color: "var(--muted)",
            fontSize: 12,
            transform: open ? "rotate(180deg)" : "rotate(0deg)",
            transition: "transform 120ms ease",
          }}
        >
          ▾
        </span>
      </button>

      {open ? (
        <div
          role="listbox"
          style={{
            position: "absolute",
            zIndex: 9999,
            left: 0,
            right: 0,
            top: "calc(100% + 8px)",
            border: "1px solid color-mix(in srgb, var(--accent), transparent 45%)",
            background: "#14213A",
            borderRadius: 14,
            boxShadow: "0 24px 60px rgba(0,0,0,.72)",
            padding: 6,
            display: "grid",
            gap: 4,
            overflow: "hidden",
          }}
        >
          {BULK_STATUS_OPTIONS.map((option) => {
            const selected = option.value === value;

            return (
              <button
                key={option.value}
                type="button"
                role="option"
                aria-selected={selected}
                onClick={() => {
                  onChange(option.value);
                  setOpen(false);
                }}
                style={{
                  border: selected
                    ? "1px solid color-mix(in srgb, var(--accent), transparent 35%)"
                    : "1px solid transparent",
                  background: selected
                    ? "color-mix(in srgb, var(--accent), transparent 78%)"
                    : "#14213A",
                  color: selected ? "var(--text)" : "var(--muted)",
                  borderRadius: 10,
                  padding: "8px 10px",
                  fontSize: 12,
                  fontWeight: 850,
                  textAlign: "left",
                  cursor: "pointer",
                }}
                onMouseEnter={(event) => {
                  if (!selected) {
                    event.currentTarget.style.background = "#1B2C4A";
                    event.currentTarget.style.color = "var(--text)";
                  }
                }}
                onMouseLeave={(event) => {
                  if (!selected) {
                    event.currentTarget.style.background = "#14213A";
                    event.currentTarget.style.color = "var(--muted)";
                  }
                }}
              >
                {option.label}
              </button>
            );
          })}
        </div>
      ) : null}
    </div>
  );
}

function OasysSelect({ value, onChange, options = [], placeholder = "Select", disabled = false }) {
  const [open, setOpen] = useState(false);
  const rootRef = useRef(null);

  const selectedOption =
    options.find((option) => String(option.value) === String(value)) ||
    options.find((option) => option.value === "") ||
    options[0] ||
    { value: "", label: placeholder };

  useEffect(() => {
    if (!open) return undefined;

    const handlePointerDown = (event) => {
      if (!rootRef.current?.contains(event.target)) {
        setOpen(false);
      }
    };

    const handleKeyDown = (event) => {
      if (event.key === "Escape") setOpen(false);
    };

    document.addEventListener("mousedown", handlePointerDown);
    document.addEventListener("keydown", handleKeyDown);

    return () => {
      document.removeEventListener("mousedown", handlePointerDown);
      document.removeEventListener("keydown", handleKeyDown);
    };
  }, [open]);

  return (
    <div
      ref={rootRef}
      style={{
        position: "relative",
        zIndex: open ? 9998 : 1,
      }}
    >
      <button
        type="button"
        disabled={disabled}
        onClick={() => setOpen((current) => !current)}
        style={statusDropdownButtonStyle(open)}
        aria-haspopup="listbox"
        aria-expanded={open}
      >
        <span
          style={{
            overflow: "hidden",
            textOverflow: "ellipsis",
            whiteSpace: "nowrap",
          }}
        >
          {selectedOption.label || placeholder}
        </span>
        <span
          style={{
            color: "var(--muted)",
            fontSize: 12,
            transform: open ? "rotate(180deg)" : "rotate(0deg)",
            transition: "transform 120ms ease",
          }}
        >
          ▾
        </span>
      </button>

      {open ? (
        <div
          role="listbox"
          style={{
            position: "absolute",
            zIndex: 9999,
            left: 0,
            right: 0,
            top: "calc(100% + 8px)",
            border: "1px solid color-mix(in srgb, var(--accent), transparent 45%)",
            background: "#14213A",
            borderRadius: 14,
            boxShadow: "0 24px 60px rgba(0,0,0,.72)",
            padding: 6,
            display: "grid",
            gap: 4,
            overflow: "hidden",
            maxHeight: 260,
            overflowY: "auto",
          }}
        >
          {options.map((option) => {
            const selected = String(option.value) === String(value);

            return (
              <button
                key={`${option.value}-${option.label}`}
                type="button"
                role="option"
                aria-selected={selected}
                onClick={() => {
                  onChange(option.value);
                  setOpen(false);
                }}
                style={{
                  border: selected
                    ? "1px solid color-mix(in srgb, var(--accent), transparent 35%)"
                    : "1px solid transparent",
                  background: selected
                    ? "color-mix(in srgb, var(--accent), transparent 78%)"
                    : "#14213A",
                  color: selected ? "var(--text)" : "var(--muted)",
                  borderRadius: 10,
                  padding: "8px 10px",
                  fontSize: 12,
                  fontWeight: 850,
                  textAlign: "left",
                  cursor: "pointer",
                  overflow: "hidden",
                  textOverflow: "ellipsis",
                  whiteSpace: "nowrap",
                }}
                onMouseEnter={(event) => {
                  if (!selected) {
                    event.currentTarget.style.background = "#1B2C4A";
                    event.currentTarget.style.color = "var(--text)";
                  }
                }}
                onMouseLeave={(event) => {
                  if (!selected) {
                    event.currentTarget.style.background = "#14213A";
                    event.currentTarget.style.color = "var(--muted)";
                  }
                }}
              >
                {option.label}
              </button>
            );
          })}
        </div>
      ) : null}
    </div>
  );
}


function formatActivityTypeLabel(value) {
  const normalized = normalizeText(value);
  if (normalized === "validation") return "Validation";
  if (normalized === "evidence") return "Evidence";
  return cleanValue(value, "Activity").replace(/_/g, " ");
}

function getValidationTone(value) {
  const normalized = normalizeText(value);
  if (normalized === "failed") return "danger";
  if (normalized === "manual_override") return "warn";
  if (normalized === "approved_exception") return "ok";
  if (normalized === "passed") return "ok";
  if (normalized === "stale") return "warn";
  return "default";
}

function getActivityTone(activity = {}) {
  const type = normalizeText(activity.activity_type);
  if (type === "evidence") return "accent";
  return getValidationTone(activity.validation_status || activity.validation_result);
}

function activityTimestamp(activity = {}) {
  return (
    activity.activity_at ||
    activity.executed_at ||
    activity.uploaded_at ||
    activity.created_at ||
    activity.updated_at ||
    null
  );
}

function activityPrimaryLabel(activity = {}) {
  if (normalizeText(activity.activity_type) === "evidence") {
    return cleanValue(activity.filename || activity.evidence_type || activity.storage_path, "Evidence attached");
  }

  const status = cleanValue(activity.validation_status || activity.validation_result, "validation").replace(/_/g, " ");
  return `Validation ${status}`;
}

function activitySecondaryLabel(activity = {}) {
  return (
    activity.failure_reason ||
    activity.validation_reason ||
    activity.evidence_ref ||
    activity.storage_path ||
    activity.task_title ||
    "No detail provided."
  );
}

function activityActorLabel(activity = {}) {
  return cleanValue(
    activity.executed_by_name || activity.uploaded_by_name || activity.executed_by || activity.uploaded_by,
    "System"
  );
}

function activityControlLabel(activity = {}) {
  const control = cleanValue(activity.control_id || activity.linked_control_id, "—");
  const rule = cleanValue(activity.rule_id, "");
  return rule ? `${control} / ${rule}` : control;
}

const ASSURANCE_METRIC_DEFINITIONS = {
  validation_runs: {
    key: "validation_runs",
    label: "Validation Runs",
    modalTitle: "Validation Run Details",
    tone: "accent",
    summary:
      "All recent validation runs recorded against remediation tasks, including passed, failed, stale, and manual override outcomes.",
  },
  failed_runs: {
    key: "failed_runs",
    label: "Failed Runs",
    modalTitle: "Failed Validation Details",
    tone: "danger",
    summary:
      "Validation runs that failed and require another remediation pass before the task can be closed.",
  },
  manual_overrides: {
    key: "manual_overrides",
    label: "Manual Overrides",
    modalTitle: "Manual Override Details",
    tone: "warn",
    summary:
      "Manual validation outcomes that were approved by an operator and preserved in the remediation audit trail.",
  },
  evidence_linked: {
    key: "evidence_linked",
    label: "Evidence Linked",
    modalTitle: "Linked Evidence Details",
    tone: "ok",
    summary:
      "Evidence attachments linked to remediation tasks to support audit-ready validation and closure.",
  },
  evidence_coverage: {
    key: "evidence_coverage",
    label: "Evidence Coverage",
    modalTitle: "Evidence Coverage Details",
    tone: "warn",
    summary:
      "Evidence coverage across remediation tasks, including how many tasks have at least one supporting evidence item.",
  },
};

function getAssuranceMetricActivities(metricKey, activities = []) {
  const rows = Array.isArray(activities) ? activities : [];

  if (metricKey === "failed_runs") {
    return rows.filter((activity) => {
      const status = normalizeText(activity.validation_status || activity.validation_result);
      return normalizeText(activity.activity_type) === "validation" && status === "failed";
    });
  }

  if (metricKey === "manual_overrides") {
    return rows.filter((activity) => {
      const status = normalizeText(activity.validation_status || activity.validation_result);
      return normalizeText(activity.activity_type) === "validation" && status === "manual_override";
    });
  }

  if (metricKey === "evidence_linked") {
    return rows.filter((activity) => normalizeText(activity.activity_type) === "evidence");
  }

  if (metricKey === "evidence_coverage") {
    return rows.filter((activity) => normalizeText(activity.activity_type) === "evidence");
  }

  return rows.filter((activity) => normalizeText(activity.activity_type) !== "evidence");
}

function getAssuranceMetricValue(metricKey, analytics = {}, evidenceCoveragePercent = 0) {
  if (metricKey === "validation_runs") return safeNumber(analytics?.validation_run_total, 0);
  if (metricKey === "failed_runs") return safeNumber(analytics?.validation_failed_count, 0);
  if (metricKey === "manual_overrides") return safeNumber(analytics?.manual_override_count, 0);
  if (metricKey === "evidence_linked") return safeNumber(analytics?.tasks_with_evidence, 0);
  if (metricKey === "evidence_coverage") return `${evidenceCoveragePercent}%`;
  return 0;
}

function getAssuranceMetricFootnote(metricKey, analytics = {}) {
  if (metricKey === "validation_runs") {
    return `${safeNumber(analytics?.validations_last_7_days, 0)} in last 7 days`;
  }

  if (metricKey === "failed_runs") {
    return `${safeNumber(analytics?.failed_validations_last_7_days, 0)} in last 7 days`;
  }

  if (metricKey === "manual_overrides") {
    return `${safeNumber(analytics?.manual_overrides_last_7_days, 0)} in last 7 days`;
  }

  if (metricKey === "evidence_linked") {
    const totalEvidence = safeNumber(analytics?.total_evidence, 0);
    return `${totalEvidence} evidence item${totalEvidence === 1 ? "" : "s"}`;
  }

  if (metricKey === "evidence_coverage") {
    const withoutEvidence = safeNumber(analytics?.tasks_without_evidence, 0);
    return `${withoutEvidence} task${withoutEvidence === 1 ? "" : "s"} without evidence`;
  }

  return "";
}

function getAssuranceMetricTone(metricKey, analytics = {}, evidenceCoveragePercent = 0) {
  if (metricKey === "validation_runs") return "accent";
  if (metricKey === "failed_runs") return safeNumber(analytics?.validation_failed_count, 0) > 0 ? "danger" : "ok";
  if (metricKey === "manual_overrides") return safeNumber(analytics?.manual_override_count, 0) > 0 ? "warn" : "default";
  if (metricKey === "evidence_linked") return safeNumber(analytics?.tasks_with_evidence, 0) > 0 ? "ok" : "warn";
  if (metricKey === "evidence_coverage") {
    return evidenceCoveragePercent >= 80 ? "ok" : evidenceCoveragePercent > 0 ? "warn" : "danger";
  }

  return "default";
}

function AssuranceMetricButton({
  metricKey,
  analytics,
  evidenceCoveragePercent,
  onOpen,
}) {
  const definition = ASSURANCE_METRIC_DEFINITIONS[metricKey] || ASSURANCE_METRIC_DEFINITIONS.validation_runs;
  const tone = getAssuranceMetricTone(metricKey, analytics, evidenceCoveragePercent);
  const color =
    tone === "danger"
      ? "var(--danger)"
      : tone === "warn"
      ? "var(--warn)"
      : tone === "ok"
      ? "var(--ok)"
      : tone === "accent"
      ? "var(--accent)"
      : "var(--text)";

  return (
    <button
      type="button"
      onClick={() => onOpen(metricKey)}
      style={{
        appearance: "none",
        border: "1px solid transparent",
        background: "transparent",
        borderRadius: 14,
        padding: 10,
        display: "grid",
        gap: 5,
        textAlign: "left",
        cursor: "pointer",
        color: "var(--text)",
        minWidth: 0,
      }}
      onMouseEnter={(event) => {
        event.currentTarget.style.borderColor = "color-mix(in srgb, var(--accent), transparent 62%)";
        event.currentTarget.style.background = "color-mix(in srgb, var(--accent), transparent 94%)";
      }}
      onMouseLeave={(event) => {
        event.currentTarget.style.borderColor = "transparent";
        event.currentTarget.style.background = "transparent";
      }}
      title={`View ${definition.label.toLowerCase()} details`}
    >
      <span style={{ fontSize: 12, fontWeight: 850 }}>{definition.label}</span>
      <span style={{ color, fontSize: 22, fontWeight: 950, lineHeight: 1 }}>
        {getAssuranceMetricValue(metricKey, analytics, evidenceCoveragePercent)}
      </span>
      <span style={{ color: "var(--muted)", fontSize: 12, lineHeight: 1.35 }}>
        {getAssuranceMetricFootnote(metricKey, analytics)}
      </span>
    </button>
  );
}


function OperationsSummaryMetric({ label, value, footnote, tone = "default", active = false, onClick }) {
  const color =
    tone === "danger"
      ? "var(--danger)"
      : tone === "warn"
      ? "var(--warn)"
      : tone === "ok"
      ? "var(--ok)"
      : tone === "accent"
      ? "var(--accent)"
      : "var(--text)";

  return (
    <button
      type="button"
      onClick={onClick}
      style={{
        appearance: "none",
        width: "100%",
        minHeight: 104,
        border: active
          ? `1px solid color-mix(in srgb, ${color}, transparent 38%)`
          : "1px solid color-mix(in srgb, var(--text), transparent 88%)",
        background: active
          ? `linear-gradient(135deg, color-mix(in srgb, ${color}, transparent 88%), color-mix(in srgb, var(--panel-2), transparent 12%))`
          : "color-mix(in srgb, var(--panel-2), transparent 18%)",
        borderRadius: 16,
        padding: "10px 11px",
        display: "grid",
        gridTemplateRows: "auto auto 1fr",
        alignContent: "start",
        gap: 6,
        textAlign: "left",
        cursor: "pointer",
        color: "var(--text)",
        minWidth: 0,
        boxShadow: active ? `0 0 0 3px color-mix(in srgb, ${color}, transparent 88%)` : "none",
      }}
      onMouseEnter={(event) => {
        event.currentTarget.style.borderColor = `color-mix(in srgb, ${color}, transparent 50%)`;
        event.currentTarget.style.background = `linear-gradient(135deg, color-mix(in srgb, ${color}, transparent 91%), color-mix(in srgb, var(--panel-2), transparent 10%))`;
      }}
      onMouseLeave={(event) => {
        event.currentTarget.style.borderColor = active
          ? `color-mix(in srgb, ${color}, transparent 38%)`
          : "color-mix(in srgb, var(--text), transparent 88%)";
        event.currentTarget.style.background = active
          ? `linear-gradient(135deg, color-mix(in srgb, ${color}, transparent 88%), color-mix(in srgb, var(--panel-2), transparent 12%))`
          : "color-mix(in srgb, var(--panel-2), transparent 18%)";
      }}
    >
      <span style={{ color: "var(--muted)", fontSize: 11, fontWeight: 900, lineHeight: 1.15 }}>{label}</span>
      <span style={{ color, fontSize: 22, fontWeight: 950, lineHeight: 1 }}>{value}</span>
      <span style={{ color: "var(--muted)", fontSize: 10.5, lineHeight: 1.3, minHeight: 14 }}>{footnote}</span>
    </button>
  );
}

function ActivityDetailRow({ activity = {}, onOpenTask }) {
  const tone = getActivityTone(activity);
  const taskId = activity.task_id;
  const canOpenTask = taskId && typeof onOpenTask === "function";

  return (
    <div
      style={{
        border: "1px solid color-mix(in srgb, var(--text), transparent 88%)",
        background: "color-mix(in srgb, var(--panel-2), transparent 16%)",
        borderRadius: 14,
        padding: 12,
        display: "grid",
        gap: 8,
      }}
    >
      <div style={{ display: "flex", alignItems: "center", justifyContent: "space-between", gap: 10, flexWrap: "wrap" }}>
        <div style={{ display: "flex", gap: 8, alignItems: "center", minWidth: 0, flexWrap: "wrap" }}>
          <span style={chipStyle(tone)}>{formatActivityTypeLabel(activity.activity_type)}</span>
          <span style={{ fontWeight: 900, fontSize: 14 }}>{activityPrimaryLabel(activity)}</span>
        </div>
        <div style={{ color: "var(--muted)", fontSize: 11, fontWeight: 750 }}>
          {formatTimestamp(activityTimestamp(activity))}
        </div>
      </div>

      <div style={{ color: "var(--muted)", fontSize: 12, lineHeight: 1.45 }}>
        {activitySecondaryLabel(activity)}
      </div>

      <div style={{ display: "grid", gridTemplateColumns: "repeat(2, minmax(0, 1fr))", gap: 8 }}>
        <div style={{ color: "var(--muted)", fontSize: 12 }}>
          <strong style={{ color: "var(--text)" }}>Control:</strong> {activityControlLabel(activity)}
        </div>
        <div style={{ color: "var(--muted)", fontSize: 12 }}>
          <strong style={{ color: "var(--text)" }}>Actor:</strong> {activityActorLabel(activity)}
        </div>
        {activity.evidence_ref || activity.storage_path ? (
          <div style={{ color: "var(--muted)", fontSize: 12, gridColumn: "1 / -1", overflowWrap: "anywhere" }}>
            <strong style={{ color: "var(--text)" }}>Evidence:</strong>{" "}
            {cleanValue(activity.evidence_ref || activity.storage_path, "—")}
          </div>
        ) : null}
        {activity.failure_reason ? (
          <div style={{ color: "var(--danger)", fontSize: 12, gridColumn: "1 / -1", lineHeight: 1.45 }}>
            <strong>Failure reason:</strong> {activity.failure_reason}
          </div>
        ) : null}
      </div>

      {canOpenTask ? (
        <div>
          <button
            className="btn btn-ghost btn-xs"
            type="button"
            onClick={() => onOpenTask({ id: taskId, ...activity })}
          >
            Review / Manage
          </button>
        </div>
      ) : null}
    </div>
  );
}

function AssuranceMetricModal({
  metricKey,
  analytics,
  evidenceCoveragePercent,
  activities = [],
  onClose,
  onOpenTask,
}) {
  if (!metricKey) return null;

  const definition = ASSURANCE_METRIC_DEFINITIONS[metricKey] || ASSURANCE_METRIC_DEFINITIONS.validation_runs;
  const rows = getAssuranceMetricActivities(metricKey, activities).slice(0, 12);

  return (
    <div
      role="dialog"
      aria-modal="true"
      aria-label={definition.modalTitle}
      style={{
        position: "fixed",
        inset: 0,
        zIndex: 2147483000,
        background: "rgba(3, 8, 20, .92)",
        display: "flex",
        alignItems: "flex-start",
        justifyContent: "center",
        padding: "92px 20px 28px",
        overflowY: "auto",
      }}
      onClick={onClose}
    >
      <div
        style={{
          width: "min(860px, calc(100vw - 40px))",
          maxHeight: "calc(100vh - 124px)",
          overflow: "auto",
          border: "1px solid color-mix(in srgb, var(--accent), transparent 45%)",
          background: "#0B1220",
          borderRadius: 20,
          boxShadow: "0 28px 90px rgba(0,0,0,.86)",
          padding: 16,
          display: "grid",
          gap: 14,
        }}
        onClick={(event) => event.stopPropagation()}
      >
        <div style={{ display: "flex", alignItems: "flex-start", justifyContent: "space-between", gap: 14 }}>
          <div>
            <div className="dash-eyebrow">Validation & Evidence</div>
            <div style={{ fontSize: 20, fontWeight: 950, marginTop: 2 }}>{definition.modalTitle}</div>
            <div style={{ color: "var(--muted)", fontSize: 13, lineHeight: 1.5, marginTop: 4 }}>
              {definition.summary}
            </div>
          </div>
          <button className="btn btn-ghost btn-sm" type="button" onClick={onClose}>
            Close
          </button>
        </div>

        <div className="hero-kpis" style={{ gridTemplateColumns: "repeat(5, minmax(0, 1fr))" }}>
          <MetricCard
            label="Validation Runs"
            value={safeNumber(analytics?.validation_run_total, 0)}
            footnote={`${safeNumber(analytics?.validations_last_7_days, 0)} in last 7 days`}
            tone="accent"
          />
          <MetricCard
            label="Failed Runs"
            value={safeNumber(analytics?.validation_failed_count, 0)}
            footnote={`${safeNumber(analytics?.failed_validations_last_7_days, 0)} in last 7 days`}
            tone={safeNumber(analytics?.validation_failed_count, 0) > 0 ? "danger" : "ok"}
          />
          <MetricCard
            label="Manual Overrides"
            value={safeNumber(analytics?.manual_override_count, 0)}
            footnote={`${safeNumber(analytics?.manual_overrides_last_7_days, 0)} in last 7 days`}
            tone={safeNumber(analytics?.manual_override_count, 0) > 0 ? "warn" : "default"}
          />
          <MetricCard
            label="Evidence Linked"
            value={safeNumber(analytics?.tasks_with_evidence, 0)}
            footnote={`${safeNumber(analytics?.total_evidence, 0)} evidence item${safeNumber(analytics?.total_evidence, 0) === 1 ? "" : "s"}`}
            tone={safeNumber(analytics?.tasks_with_evidence, 0) > 0 ? "ok" : "warn"}
          />
          <MetricCard
            label="Evidence Coverage"
            value={`${evidenceCoveragePercent}%`}
            footnote={`${safeNumber(analytics?.tasks_without_evidence, 0)} without evidence`}
            tone={evidenceCoveragePercent >= 80 ? "ok" : evidenceCoveragePercent > 0 ? "warn" : "danger"}
          />
        </div>

        <div
          style={{
            border: "1px solid color-mix(in srgb, var(--text), transparent 88%)",
            borderRadius: 16,
            padding: 12,
            display: "grid",
            gap: 10,
          }}
        >
          <div style={{ display: "flex", alignItems: "center", justifyContent: "space-between", gap: 10 }}>
            <div>
              <div style={{ fontWeight: 900, fontSize: 14 }}>
                {definition.label} Activity
              </div>
              <div style={{ color: "var(--muted)", fontSize: 12, lineHeight: 1.45, marginTop: 2 }}>
                Showing the latest matching audit events from the remediation analytics feed.
              </div>
            </div>
            <span style={chipStyle(definition.tone)}>{rows.length} shown</span>
          </div>

          {metricKey === "evidence_coverage" ? (
            <div
              style={{
                border: "1px solid color-mix(in srgb, var(--warn), transparent 72%)",
                background: "color-mix(in srgb, var(--warn), transparent 92%)",
                borderRadius: 14,
                padding: 12,
                color: "var(--muted)",
                fontSize: 13,
                lineHeight: 1.5,
              }}
            >
              <strong style={{ color: "var(--text)" }}>{safeNumber(analytics?.tasks_without_evidence, 0)}</strong>{" "}
              remediation task{safeNumber(analytics?.tasks_without_evidence, 0) === 1 ? "" : "s"} currently have no linked evidence.
              Use Review / Manage on task rows to attach or review supporting evidence before closure.
            </div>
          ) : null}

          {rows.length === 0 ? (
            <div style={{ color: "var(--muted)", fontSize: 13, lineHeight: 1.5 }}>
              No matching recent activity is available for this metric yet.
            </div>
          ) : (
            <div style={{ display: "grid", gap: 10 }}>
              {rows.map((activity, index) => (
                <ActivityDetailRow
                  key={`${metricKey}-${activity.activity_type || "activity"}-${activity.id || index}`}
                  activity={activity}
                  onOpenTask={onOpenTask}
                />
              ))}
            </div>
          )}
        </div>
      </div>
    </div>
  );
}


export default function RemediationOperationsPanel({
  framework,
  fetchToken,
  getJsonWithToken,
  postJsonWithToken,
  refreshKey = 0,
  onOpenTask,
  onOpenFixPlan,
  onOpenRiskQueue,
  onOpenAllActiveTasksModal,
  onOpenQueueStatusTasksModal,
  onOpenValidationFailedModal,
  onOpenOverdueTasksModal,
  onOpenInProgressTasksModal,
  onOpenClosedTasksModal,
  onOpenApprovedExceptionsModal,
  onStartTask,
  onMarkReady,
  onRunValidation,
  isValidationRunning = false,
  currentUserId = "",
  currentUserName = "",
  activeSnapshotKey = "",
}) {
  const [activeTab, setActiveTab] = useState("all");
  const [analytics, setAnalytics] = useState(null);
  const [queuePayload, setQueuePayload] = useState(null);
  const [snapshotQueuePayload, setSnapshotQueuePayload] = useState(null);
  const [loading, setLoading] = useState(false);
  const [loadingMore, setLoadingMore] = useState(false);
  const [err, setErr] = useState("");
  const [selectedTaskIds, setSelectedTaskIds] = useState(() => new Set());
  const [bulkOwnerUserId, setBulkOwnerUserId] = useState("");
  const [bulkOwnerName, setBulkOwnerName] = useState("");
  const [bulkPriority, setBulkPriority] = useState("");
  const [bulkDueDate, setBulkDueDate] = useState("");
  const [bulkStatus, setBulkStatus] = useState("in_progress");
  const [bulkNote, setBulkNote] = useState("");
  const [bulkBusy, setBulkBusy] = useState(false);
  const [bulkNotice, setBulkNotice] = useState("");
  const [bulkError, setBulkError] = useState("");
  const [workflowBusyTaskId, setWorkflowBusyTaskId] = useState("");
  const [workflowNotice, setWorkflowNotice] = useState("");
  const [workflowError, setWorkflowError] = useState("");
  const [operationsSearch, setOperationsSearch] = useState("");
  const [debouncedOperationsSearch, setDebouncedOperationsSearch] = useState("");
  const [ownerFilter, setOwnerFilter] = useState("");
  const [priorityFilter, setPriorityFilter] = useState("");
  const [slaFilter, setSlaFilter] = useState("");
  const [assignmentStateFilter, setAssignmentStateFilter] = useState("");
  const [quickView, setQuickView] = useState("");
  const [selectedAssuranceMetric, setSelectedAssuranceMetric] = useState(null);
  const assuranceCardRef = useRef(null);
  const taskListRef = useRef(null);

  const activeTabConfig = useMemo(
    () => QUEUE_TABS.find((tab) => tab.key === activeTab) || QUEUE_TABS[0],
    [activeTab]
  );

  const normalizedActiveSnapshotKey = useMemo(() => {
    const key = normalizeText(activeSnapshotKey || "").replace(/[\s-]+/g, "_");
    return key === "ready" ? "ready_for_validation" : key;
  }, [activeSnapshotKey]);

  const isSnapshotCardActive = useCallback(
    (key) => normalizedActiveSnapshotKey === key,
    [normalizedActiveSnapshotKey]
  );


  const quickViews = useMemo(
    () => [
      {
        key: "my_open_tasks",
        label: "My Open Tasks",
        description: "Open tasks assigned to the current user, including overdue work",
        disabled: !currentUserId && !currentUserName,
      },
      { key: "validation_failed", label: "Validation Failed", description: "Tasks that need another fix pass" },
      { key: "due_soon", label: "Due Soon", description: "Tasks nearing their SLA deadline" },
      { key: "high_priority", label: "High Priority", description: "Priority 100 work" },
      { key: "unassigned", label: "Unassigned", description: "Tasks without an owner" },
    ],
    [currentUserId, currentUserName]
  );

  useEffect(() => {
    const timeoutId = window.setTimeout(() => {
      setDebouncedOperationsSearch(operationsSearch.trim());
    }, 350);

    return () => window.clearTimeout(timeoutId);
  }, [operationsSearch]);

  const loadOperations = useCallback(
    async ({ append = false, offset = 0 } = {}) => {
      if (typeof fetchToken !== "function" || typeof getJsonWithToken !== "function") return;

      if (append) {
        setLoadingMore(true);
      } else {
        setLoading(true);
      }
      setErr("");

      const controller = new AbortController();

      try {
        const token = await fetchToken();
        const includeClosedQueue = Boolean(activeTabConfig.params?.includeClosed);
        const shouldRefreshSnapshot = !append;
        const [analyticsPayload, queueResult, snapshotQueueResult] = await Promise.all([
          fetchRemediationOperationsAnalytics({
            getJsonWithToken,
            token,
            framework,
            includeClosed: true,
            signal: controller.signal,
          }),
          fetchRemediationOperationsQueue({
            getJsonWithToken,
            token,
            framework,
            includeClosed: includeClosedQueue,
            limit: OPERATIONS_QUEUE_PAGE_SIZE,
            offset,
            search: debouncedOperationsSearch,
            // "My Open Tasks" should match the visible owner name used on
            // remediation tasks. Passing the authenticated user id as an
            // owner_user_id can over-filter when task ownership is name-based.
            ownerName: ownerFilter || (quickView === "my_open_tasks" ? currentUserName : ""),
            priority: priorityFilter,
            assignmentState: assignmentStateFilter,
            slaState: slaFilter,
            ...activeTabConfig.params,
            signal: controller.signal,
          }),
          shouldRefreshSnapshot
            ? fetchRemediationOperationsQueue({
                getJsonWithToken,
                token,
                framework,
                includeClosed: false,
                limit: 500,
                offset: 0,
                signal: controller.signal,
              })
            : Promise.resolve(null),
        ]);

        setAnalytics(analyticsPayload || null);
        setQueuePayload((current) =>
          append ? mergeQueuePayload(current, queueResult) : queueResult || null
        );
        if (shouldRefreshSnapshot && snapshotQueueResult) {
          setSnapshotQueuePayload(snapshotQueueResult);
        }
      } catch (error) {
        if (error?.name !== "AbortError") {
          console.error("Failed to load remediation operations", error);
          setErr(String(error?.message || error || "Unable to load remediation operations."));
        }
      } finally {
        if (append) {
          setLoadingMore(false);
        } else {
          setLoading(false);
        }
      }
    },
    [
      activeTabConfig.params,
      assignmentStateFilter,
      currentUserName,
      debouncedOperationsSearch,
      fetchToken,
      framework,
      getJsonWithToken,
      ownerFilter,
      priorityFilter,
      quickView,
      slaFilter,
    ]
  );

  useEffect(() => {
    loadOperations({ append: false, offset: 0 });
  }, [loadOperations, refreshKey]);

  const tasks = useMemo(() => getQueueTasks(queuePayload), [queuePayload]);
  const snapshotTasks = useMemo(() => getQueueTasks(snapshotQueuePayload), [snapshotQueuePayload]);

  const isActiveSnapshotTask = useCallback(
    (task = {}) => !isClosedQueueTask(task) && !isApprovedExceptionTask(task),
    []
  );

  const snapshotCounts = useMemo(() => {
    const rows = (Array.isArray(snapshotTasks) ? snapshotTasks : []).filter(isActiveSnapshotTask);

    const isUnassignedTask = (task = {}) => {
      const owner = cleanValue(task.owner_name || task.ownerName || task.owner_user_id || task.ownerUserId, "");
      return !owner || normalizeText(owner) === "unassigned";
    };

    const countByStatus = (acceptedStatuses = []) =>
      rows.filter((task) => acceptedStatuses.includes(normalizeText(task.status || task.task_status || "open"))).length;

    return {
      open: countByStatus(["open", "assigned", "deferred"]),
      inProgress: countByStatus(["in_progress"]),
      ready: rows.filter((task) => {
        const status = normalizeText(task.status || task.task_status || "");
        const validationStatus = normalizeText(task.validation_status || task.validationStatus || "");
        return status === "ready_for_validation" && validationStatus !== "failed";
      }).length,
      validationFailed: rows.filter((task) => normalizeText(task.validation_status || task.validationStatus || "") === "failed").length,
      overdue: rows.filter((task) => isTaskOverdue(task)).length,
      unassigned: rows.filter(isUnassignedTask).length,
    };
  }, [isActiveSnapshotTask, snapshotTasks]);

  const isClosedQueue = activeTab === "closed";
  const displayedTasks = useMemo(
    () => (isClosedQueue ? sortClosedTasksMostRecentFirst(tasks) : tasks),
    [isClosedQueue, tasks]
  );

  const queueTotal = safeNumber(queuePayload?.total, tasks.length);
  const queueReturned = safeNumber(queuePayload?.returned, tasks.length);
  const hasMoreTasks = queueTotal > tasks.length;

  const ownerFilterOptions = useMemo(() => {
    const seen = new Map();
    const backendOwners = Array.isArray(queuePayload?.filter_options?.owners)
      ? queuePayload.filter_options.owners
      : [];

    backendOwners.forEach((owner) => {
      const value = cleanValue(owner?.owner_name || owner?.owner_user_id, "");
      if (!value || normalizeText(value) === "unassigned") return;
      const key = normalizeText(value);
      const total = safeNumber(owner?.total, null);
      const label = total !== null && Number.isFinite(total) ? `${value} (${total})` : value;
      if (!seen.has(key)) seen.set(key, { value, label });
    });

    tasks.forEach((task) => {
      const value = cleanValue(task.owner_name || task.owner_user_id, "");
      if (!value || normalizeText(value) === "unassigned") return;
      const key = normalizeText(value);
      if (!seen.has(key)) seen.set(key, { value, label: value });
    });

    return Array.from(seen.values()).sort((a, b) => a.value.localeCompare(b.value));
  }, [queuePayload?.filter_options?.owners, tasks]);

  const priorityFilterOptions = useMemo(() => {
    const seen = new Map();
    const backendPriorities = Array.isArray(queuePayload?.filter_options?.priorities)
      ? queuePayload.filter_options.priorities
      : [];

    backendPriorities.forEach((item) => {
      const priority = safeNumber(item?.priority, null);
      if (priority === null || !Number.isFinite(priority)) return;
      const key = String(priority);
      const total = safeNumber(item?.total, null);
      const label = total !== null && Number.isFinite(total) ? `P${priority} (${total})` : `P${priority}`;
      if (!seen.has(key)) seen.set(key, { value: key, label, priority });
    });

    tasks.forEach((task) => {
      const priority = safeNumber(task.priority, null);
      if (priority === null || !Number.isFinite(priority)) return;
      const key = String(priority);
      if (!seen.has(key)) seen.set(key, { value: key, label: `P${priority}`, priority });
    });

    return Array.from(seen.values()).sort((a, b) => b.priority - a.priority);
  }, [queuePayload?.filter_options?.priorities, tasks]);

  const slaFilterOptions = useMemo(() => {
    const seen = new Map();
    const backendSlaStates = Array.isArray(queuePayload?.filter_options?.sla_states)
      ? queuePayload.filter_options.sla_states
      : [];

    backendSlaStates.forEach((item) => {
      const value = cleanValue(item?.sla_state, "");
      if (!value) return;
      const key = normalizeText(value);
      const total = safeNumber(item?.total, null);
      const display = formatSlaStateLabel(value);
      const label = total !== null && Number.isFinite(total) ? `${display} (${total})` : display;
      if (!seen.has(key)) seen.set(key, { value, label });
    });

    tasks.forEach((task) => {
      const value = cleanValue(task.sla_state, "");
      if (!value) return;
      const key = normalizeText(value);
      if (!seen.has(key)) seen.set(key, { value, label: formatSlaStateLabel(value) });
    });

    return Array.from(seen.values()).sort((a, b) => a.value.localeCompare(b.value));
  }, [queuePayload?.filter_options?.sla_states, tasks]);

  const visibleTaskIds = useMemo(
    () => displayedTasks.map((task) => task.id).filter(Boolean),
    [displayedTasks]
  );

  const selectedTasks = useMemo(
    () => displayedTasks.filter((task) => selectedTaskIds.has(task.id)),
    [displayedTasks, selectedTaskIds]
  );

  const selectedCount = selectedTasks.length;

  const allVisibleSelected =
    visibleTaskIds.length > 0 && visibleTaskIds.every((id) => selectedTaskIds.has(id));

  const queueDistribution = useMemo(
    () => analytics?.queue_distribution || [],
    [analytics?.queue_distribution]
  );

  const latestRemediationActivity = useMemo(
    () => (Array.isArray(analytics?.latest_remediation_activity) ? analytics.latest_remediation_activity : []),
    [analytics?.latest_remediation_activity]
  );

  const evidenceCoveragePercent = useMemo(() => {
    const totalTasks = safeNumber(analytics?.total_tasks, 0);
    const tasksWithEvidence = safeNumber(analytics?.tasks_with_evidence, 0);
    if (!totalTasks) return 0;
    return Math.round((tasksWithEvidence / totalTasks) * 100);
  }, [analytics?.tasks_with_evidence, analytics?.total_tasks]);

  const openAssuranceMetric = useCallback((metricKey) => {
    setSelectedAssuranceMetric(metricKey);
  }, []);

  const closeAssuranceMetric = useCallback(() => {
    setSelectedAssuranceMetric(null);

    window.requestAnimationFrame(() => {
      const card = assuranceCardRef.current;
      if (!card) return;

      const top = card.getBoundingClientRect().top + window.scrollY - 96;
      window.scrollTo({
        top: Math.max(top, 0),
        behavior: "smooth",
      });
    });
  }, []);

  useEffect(() => {
    setSelectedTaskIds(new Set());
    setBulkNotice("");
    setBulkError("");
    setBulkNote("");
    setWorkflowNotice("");
    setWorkflowError("");
    setOperationsSearch("");
    setDebouncedOperationsSearch("");
    setOwnerFilter("");
    setPriorityFilter("");
    setSlaFilter("");
    setAssignmentStateFilter("");
    setQuickView("");
  }, [framework]);

  useEffect(() => {
    setSelectedTaskIds(new Set());
    setBulkError("");
  }, [assignmentStateFilter, debouncedOperationsSearch, ownerFilter, priorityFilter, slaFilter]);

  useEffect(() => {
    if (!bulkNotice) return undefined;

    const timeoutId = window.setTimeout(() => {
      setBulkNotice("");
    }, BULK_NOTICE_AUTO_CLEAR_MS);

    return () => window.clearTimeout(timeoutId);
  }, [bulkNotice]);

  useEffect(() => {
    if (!workflowNotice) return undefined;

    const timeoutId = window.setTimeout(() => {
      setWorkflowNotice("");
    }, BULK_NOTICE_AUTO_CLEAR_MS);

    return () => window.clearTimeout(timeoutId);
  }, [workflowNotice]);

  const tabTotal = useCallback(
    (tab) => {
      if (tab.key === "all") return safeNumber(analytics?.active_tasks, safeNumber(snapshotQueuePayload?.total, snapshotTasks.length));
      if (tab.key === "open") return snapshotCounts.open;
      if (tab.key === "in_progress") return snapshotCounts.inProgress || safeNumber(analytics?.in_progress_tasks, 0);
      if (tab.key === "ready_for_validation") return snapshotCounts.ready;
      if (tab.key === "validation_failed") return snapshotCounts.validationFailed || safeNumber(analytics?.failed_validation_tasks, 0);
      if (tab.key === "overdue") return snapshotCounts.overdue || safeNumber(analytics?.overdue_tasks, 0);
      if (tab.key === "closed") return safeNumber(analytics?.closed_tasks, 0);
      if (tab.key === "approved_exception") return getApprovedExceptionTotal(analytics, tasks);
      return getDistributionTotal(queueDistribution, tab.params?.queue || tab.key);
    },
    [analytics, queueDistribution, snapshotCounts, snapshotQueuePayload?.total, snapshotTasks.length, tasks]
  );

  const filtersActive = Boolean(
    operationsSearch.trim() || ownerFilter || priorityFilter || slaFilter || assignmentStateFilter || quickView
  );

  const clearOperationsFilters = useCallback(() => {
    setActiveTab("all");
    setOperationsSearch("");
    setDebouncedOperationsSearch("");
    setOwnerFilter("");
    setPriorityFilter("");
    setSlaFilter("");
    setAssignmentStateFilter("");
    setQuickView("");
  }, []);

  const clearSingleOperationsFilter = useCallback((filterKey) => {
    if (filterKey === "search") setOperationsSearch("");
    if (filterKey === "owner") setOwnerFilter("");
    if (filterKey === "priority") setPriorityFilter("");
    if (filterKey === "sla") setSlaFilter("");
    if (filterKey === "assignment") setAssignmentStateFilter("");
    if (filterKey === "quick_view") setQuickView("");
  }, []);

  const activeFilterChips = useMemo(() => {
    const chips = [];
    const searchValue = operationsSearch.trim();

    if (quickView) {
      const activeQuickView = quickViews.find((view) => view.key === quickView);
      chips.push({ key: "quick_view", label: `View: ${activeQuickView?.label || quickView.replace(/_/g, " ")}` });
    }
    if (searchValue) {
      chips.push({ key: "search", label: `Search: ${searchValue}` });
    }
    if (ownerFilter) {
      chips.push({ key: "owner", label: `Owner: ${ownerFilter}` });
    }
    if (priorityFilter) {
      chips.push({ key: "priority", label: `Priority: P${priorityFilter}` });
    }
    if (slaFilter) {
      chips.push({ key: "sla", label: `SLA: ${formatSlaStateLabel(slaFilter)}` });
    }
    if (assignmentStateFilter) {
      chips.push({ key: "assignment", label: `Assignment: ${String(assignmentStateFilter).replace(/_/g, " ")}` });
    }

    return chips;
  }, [assignmentStateFilter, operationsSearch, ownerFilter, priorityFilter, quickView, quickViews, slaFilter]);

  const scrollToTaskList = useCallback(() => {
    window.requestAnimationFrame(() => {
      window.setTimeout(() => {
        const target = taskListRef.current;
        if (!target) return;

        const top = target.getBoundingClientRect().top + window.scrollY - 96;
        window.scrollTo({
          top: Math.max(top, 0),
          behavior: "smooth",
        });
      }, 80);
    });
  }, []);

  const handleQueueTabClick = useCallback((tabKey) => {
    setActiveTab(tabKey);
    setSelectedTaskIds(new Set());
    setBulkError("");
    setOperationsSearch("");
    setDebouncedOperationsSearch("");
    setOwnerFilter("");
    setPriorityFilter("");
    setSlaFilter("");
    setAssignmentStateFilter("");
    setQuickView("");
    scrollToTaskList();
  }, [scrollToTaskList]);

  const applyQuickView = useCallback(
    (viewKey) => {
      setSelectedTaskIds(new Set());
      setBulkError("");
      setOperationsSearch("");
      setDebouncedOperationsSearch("");
      setOwnerFilter("");
      setPriorityFilter("");
      setSlaFilter("");
      setAssignmentStateFilter("");
      setQuickView(viewKey);

      if (viewKey === "my_open_tasks") {
        setActiveTab("open");
        if (currentUserName) setOwnerFilter(currentUserName);
        scrollToTaskList();
        return;
      }

      if (viewKey === "validation_failed") {
        setActiveTab("validation_failed");
        scrollToTaskList();
        return;
      }


      if (viewKey === "due_soon") {
        setActiveTab("all");
        setSlaFilter("due_soon");
        scrollToTaskList();
        return;
      }

      if (viewKey === "high_priority") {
        setActiveTab("all");
        setPriorityFilter("100");
        scrollToTaskList();
        return;
      }

      if (viewKey === "unassigned") {
        setActiveTab("all");
        setAssignmentStateFilter("unassigned");
        scrollToTaskList();
      }
    },
    [currentUserName, scrollToTaskList]
  );

  const resetBulkFormAfterSuccess = useCallback(() => {
    setSelectedTaskIds(new Set());
    setBulkOwnerUserId("");
    setBulkOwnerName("");
    setBulkPriority("");
    setBulkDueDate("");
    setBulkStatus("in_progress");
    setBulkNote("");
    setBulkError("");
  }, []);

  const toggleTaskSelected = useCallback((taskId) => {
    if (!taskId) return;
    setSelectedTaskIds((current) => {
      const next = new Set(current);
      if (next.has(taskId)) next.delete(taskId);
      else next.add(taskId);
      return next;
    });
    setBulkError("");
  }, []);

  const toggleSelectAllVisible = useCallback(() => {
    setSelectedTaskIds((current) => {
      const next = new Set(current);
      if (visibleTaskIds.length === 0) return next;

      const shouldClear = visibleTaskIds.every((id) => next.has(id));
      if (shouldClear) {
        visibleTaskIds.forEach((id) => next.delete(id));
      } else {
        visibleTaskIds.forEach((id) => next.add(id));
      }
      return next;
    });
    setBulkError("");
  }, [visibleTaskIds]);

  const clearSelection = useCallback(() => {
    setSelectedTaskIds(new Set());
    setBulkNotice("");
    setBulkError("");
    setBulkNote("");
  }, []);

  const refreshAfterBulkAction = useCallback(
    async ({ resetForm = false } = {}) => {
      await loadOperations();

      if (resetForm) {
        resetBulkFormAfterSuccess();
        return;
      }

      setSelectedTaskIds(new Set());
    },
    [loadOperations, resetBulkFormAfterSuccess]
  );

  const handleLoadMore = useCallback(async () => {
    if (loadingMore || loading || !hasMoreTasks) return;
    await loadOperations({ append: true, offset: tasks.length });
  }, [hasMoreTasks, loadOperations, loading, loadingMore, tasks.length]);

  const handleBulkAssign = useCallback(async () => {
    setBulkNotice("");
    setBulkError("");

    if (selectedCount === 0) {
      setBulkError("Select one or more remediation tasks first.");
      return;
    }
    if (typeof postJsonWithToken !== "function" || typeof fetchToken !== "function") {
      setBulkError("Bulk assignment is not available because the API client is not wired.");
      return;
    }

    const payload = {
      task_ids: selectedTasks.map((task) => task.id),
      note: bulkNote || undefined,
    };

    if (bulkOwnerUserId.trim()) payload.owner_user_id = bulkOwnerUserId.trim();
    if (bulkOwnerName.trim()) payload.owner_name = bulkOwnerName.trim();
    if (bulkDueDate) payload.due_date = bulkDueDate;
    if (bulkPriority !== "") payload.priority = Number(bulkPriority);

    if (
      payload.owner_user_id === undefined &&
      payload.owner_name === undefined &&
      payload.due_date === undefined &&
      payload.priority === undefined
    ) {
      setBulkError("Enter an owner, priority, or due date before applying bulk assignment.");
      return;
    }

    setBulkBusy(true);
    try {
      const token = await fetchToken();
      const result = await bulkAssignRemediationTasks({
        postJsonWithToken,
        token,
        payload,
      });

      await refreshAfterBulkAction({ resetForm: true });
      setBulkNotice(
        `Bulk assignment complete: ${safeNumber(result?.updated_count, 0)} updated, ${safeNumber(result?.skipped_count, 0)} skipped.`
      );
    } catch (error) {
      console.error("Bulk assignment failed", error);
      setBulkError(String(error?.message || error || "Bulk assignment failed."));
    } finally {
      setBulkBusy(false);
    }
  }, [
    bulkDueDate,
    bulkNote,
    bulkOwnerName,
    bulkOwnerUserId,
    bulkPriority,
    fetchToken,
    postJsonWithToken,
    refreshAfterBulkAction,
    selectedCount,
    selectedTasks,
  ]);

  const handleBulkStatus = useCallback(async () => {
    setBulkNotice("");
    setBulkError("");

    if (selectedCount === 0) {
      setBulkError("Select one or more remediation tasks first.");
      return;
    }
    if (!bulkStatus) {
      setBulkError("Choose a status before applying bulk status update.");
      return;
    }
    if (typeof postJsonWithToken !== "function" || typeof fetchToken !== "function") {
      setBulkError("Bulk status update is not available because the API client is not wired.");
      return;
    }

    setBulkBusy(true);
    try {
      const token = await fetchToken();
      const result = await bulkUpdateRemediationStatus({
        postJsonWithToken,
        token,
        payload: {
          task_ids: selectedTasks.map((task) => task.id),
          status: bulkStatus,
          note: bulkNote || undefined,
        },
      });

      await refreshAfterBulkAction({ resetForm: true });
      setBulkNotice(
        `Bulk status update complete: ${safeNumber(result?.updated_count, 0)} updated, ${safeNumber(result?.skipped_count, 0)} skipped.`
      );
    } catch (error) {
      console.error("Bulk status update failed", error);
      setBulkError(String(error?.message || error || "Bulk status update failed."));
    } finally {
      setBulkBusy(false);
    }
  }, [
    bulkNote,
    bulkStatus,
    fetchToken,
    postJsonWithToken,
    refreshAfterBulkAction,
    selectedCount,
    selectedTasks,
  ]);



  const handleWorkflowAction = useCallback(
    async (task, actionKey) => {
      if (!task?.id || !actionKey) return;

      setWorkflowNotice("");
      setWorkflowError("");
      setWorkflowBusyTaskId(task.id);

      try {
        if (actionKey === "start_work") {
          if (typeof onStartTask !== "function") {
            throw new Error("Start Work action is not wired.");
          }
          await onStartTask(task);
          setWorkflowNotice("Task moved to In Progress.");
          await loadOperations();
          return;
        }

        if (actionKey === "mark_ready") {
          if (typeof onMarkReady !== "function") {
            throw new Error("Ready for Validation action is not wired.");
          }
          await onMarkReady(task);
          setWorkflowNotice("Ready for Validation action submitted.");
          await loadOperations();
          return;
        }

        if (actionKey === "run_validation") {
          if (typeof onRunValidation !== "function") {
            throw new Error("Validation action is not wired.");
          }
          await onRunValidation(task);
          setWorkflowNotice("Validation check started.");
          await loadOperations();
        }
      } catch (error) {
        console.error("Remediation workflow action failed", error);
        setWorkflowError(String(error?.message || error || "Workflow action failed."));
      } finally {
        setWorkflowBusyTaskId("");
      }
    },
    [loadOperations, onMarkReady, onRunValidation, onStartTask]
  );
  return (
    <section className="card remediation-operations-panel">
      <div className="card-header remediation-operations-panel__header">
        <div>
          <div className="dash-eyebrow">OASYS · Remediation Operations</div>
          <div className="card-title">Remediation Operations Center</div>
          <div
            style={{
              color: "var(--muted)",
              fontSize: 12,
              lineHeight: 1.45,
              marginTop: 4,
            }}
          >
            Track assigned remediation work, validation failures, SLA state, and operational queues for {framework}.
          </div>
        </div>

        <div style={{ display: "flex", gap: 8, flexWrap: "wrap", justifyContent: "flex-end" }}>
          {typeof onOpenRiskQueue === "function" ? (
            <Button
              variant="secondary"
              size="small"
              onClick={onOpenRiskQueue}
            >
              Open Risk Queue
            </Button>
          ) : null}

          <Button
            variant="secondary"
            size="small"
            onClick={loadOperations}
            loading={loading}
            loadingLabel="Refreshing…"
          >
            Refresh Ops
          </Button>
        </div>
      </div>

      <div className="card-body remediation-operations-panel__body">
        {err ? (
          <div
            style={{
              border: "1px solid color-mix(in srgb, var(--danger), transparent 72%)",
              background: "color-mix(in srgb, var(--danger), transparent 92%)",
              color: "var(--danger)",
              borderRadius: 14,
              padding: "10px 12px",
              fontSize: 13,
              lineHeight: 1.5,
              fontWeight: 700,
            }}
          >
            {err}
          </div>
        ) : null}

        {workflowNotice ? (
          <div style={{ ...chipStyle("ok"), justifySelf: "start", borderRadius: 12 }}>
            {workflowNotice}
          </div>
        ) : null}

        {workflowError ? (
          <div style={{ ...chipStyle("danger"), justifySelf: "start", borderRadius: 12 }}>
            {workflowError}
          </div>
        ) : null}

        <div
          className="remediation-operations-panel__snapshot"
          style={{
            border: "1px solid color-mix(in srgb, var(--text), transparent 88%)",
            background: "linear-gradient(135deg, color-mix(in srgb, var(--panel-2), transparent 10%), color-mix(in srgb, var(--panel), transparent 8%))",
            borderRadius: 18,
            padding: 12,
            display: "grid",
            gap: 10,
          }}
        >
          <div style={{ display: "flex", alignItems: "center", justifyContent: "space-between", gap: 12, flexWrap: "wrap" }}>
            <div>
              <div className="dash-eyebrow">Work Queue Snapshot</div>
              <div style={{ color: "var(--muted)", fontSize: 12, lineHeight: 1.4, marginTop: 3 }}>
                Status and risk views may overlap. Use All Active for the unique active work total.
              </div>
            </div>
            {activeTab !== "all" || filtersActive ? (
              <button className="btn btn-ghost btn-xs" type="button" onClick={clearOperationsFilters}>
                Clear Queue
              </button>
            ) : null}
          </div>

          <div
            style={{
              display: "grid",
              gridTemplateColumns: "repeat(9, minmax(0, 1fr))",
              gap: 8,
              alignItems: "stretch",
            }}
          >
            <OperationsSummaryMetric
              label="All Active"
              value={safeNumber(analytics?.active_tasks, 0)}
              footnote="active work"
              tone="accent"
              active={isSnapshotCardActive("all")}
              onClick={() => {
                if (typeof onOpenAllActiveTasksModal === "function") {
                  onOpenAllActiveTasksModal();
                  return;
                }
                handleQueueTabClick("all");
              }}
            />
            <OperationsSummaryMetric
              label="Open"
              value={tabTotal(QUEUE_TABS.find((tab) => tab.key === "open") || {})}
              footnote="not started"
              tone="default"
              active={isSnapshotCardActive("open")}
              onClick={() => {
                if (typeof onOpenQueueStatusTasksModal === "function") {
                  onOpenQueueStatusTasksModal("open", {
                    expectedTotal: tabTotal(QUEUE_TABS.find((tab) => tab.key === "open") || {}),
                  });
                  return;
                }
                handleQueueTabClick("open");
              }}
            />
            <OperationsSummaryMetric
              label="In Progress"
              value={tabTotal(QUEUE_TABS.find((tab) => tab.key === "in_progress") || {})}
              footnote="being worked"
              tone={tabTotal(QUEUE_TABS.find((tab) => tab.key === "in_progress") || {}) > 0 ? "warn" : "default"}
              active={isSnapshotCardActive("in_progress")}
              onClick={() => {
                if (typeof onOpenInProgressTasksModal === "function") {
                  onOpenInProgressTasksModal();
                  return;
                }
                handleQueueTabClick("in_progress");
              }}
            />
            <OperationsSummaryMetric
              label="Ready"
              value={tabTotal(QUEUE_TABS.find((tab) => tab.key === "ready_for_validation") || {})}
              footnote="for validation"
              tone="accent"
              active={isSnapshotCardActive("ready_for_validation")}
              onClick={() => {
                if (typeof onOpenQueueStatusTasksModal === "function") {
                  onOpenQueueStatusTasksModal("ready_for_validation", {
                    expectedTotal: tabTotal(QUEUE_TABS.find((tab) => tab.key === "ready_for_validation") || {}),
                  });
                  return;
                }
                handleQueueTabClick("ready_for_validation");
              }}
            />
            <OperationsSummaryMetric
              label="Validation Failed"
              value={tabTotal(QUEUE_TABS.find((tab) => tab.key === "validation_failed") || {})}
              footnote="needs fix pass"
              tone={tabTotal(QUEUE_TABS.find((tab) => tab.key === "validation_failed") || {}) > 0 ? "danger" : "ok"}
              active={isSnapshotCardActive("validation_failed")}
              onClick={() => {
                if (typeof onOpenValidationFailedModal === "function") {
                  onOpenValidationFailedModal();
                  return;
                }
                handleQueueTabClick("validation_failed");
              }}
            />
            <OperationsSummaryMetric
              label="Overdue"
              value={tabTotal(QUEUE_TABS.find((tab) => tab.key === "overdue") || {})}
              footnote="past due"
              tone={tabTotal(QUEUE_TABS.find((tab) => tab.key === "overdue") || {}) > 0 ? "danger" : "ok"}
              active={isSnapshotCardActive("overdue")}
              onClick={() => {
                if (typeof onOpenOverdueTasksModal === "function") {
                  onOpenOverdueTasksModal();
                  return;
                }
                handleQueueTabClick("overdue");
              }}
            />
            <OperationsSummaryMetric
              label="Approved Exceptions"
              value={getApprovedExceptionTotal(analytics, tasks)}
              footnote="accepted risk"
              tone={getApprovedExceptionTotal(analytics, tasks) > 0 ? "warn" : "default"}
              active={isSnapshotCardActive("approved_exception")}
              onClick={() => {
                if (typeof onOpenApprovedExceptionsModal === "function") {
                  onOpenApprovedExceptionsModal();
                  return;
                }
                handleQueueTabClick("approved_exception");
              }}
            />
            <OperationsSummaryMetric
              label="Closed"
              value={safeNumber(analytics?.closed_tasks, 0)}
              footnote="audit records"
              tone="ok"
              active={isSnapshotCardActive("closed")}
              onClick={() => {
                if (typeof onOpenClosedTasksModal === "function") {
                  onOpenClosedTasksModal();
                  return;
                }
                handleQueueTabClick("closed");
              }}
            />
            <OperationsSummaryMetric
              label="Unassigned"
              value={snapshotCounts.unassigned}
              footnote="missing owner"
              tone={snapshotCounts.unassigned > 0 ? "warn" : "ok"}
              active={isSnapshotCardActive("unassigned")}
              onClick={() => {
                if (typeof onOpenQueueStatusTasksModal === "function") {
                  onOpenQueueStatusTasksModal("unassigned", {
                    expectedTotal: snapshotCounts.unassigned,
                  });
                  return;
                }
                setActiveTab("all");
                setSelectedTaskIds(new Set());
                setOperationsSearch("");
                setDebouncedOperationsSearch("");
                setOwnerFilter("");
                setPriorityFilter("");
                setSlaFilter("");
                setAssignmentStateFilter("unassigned");
                setQuickView("unassigned");
                scrollToTaskList();
              }}
            />
          </div>
        </div>

        <div
          className="remediation-operations-panel__assurance"
          style={{
            border: "1px solid color-mix(in srgb, var(--accent), transparent 72%)",
            background: "linear-gradient(135deg, color-mix(in srgb, var(--accent), transparent 94%), color-mix(in srgb, var(--panel-2), transparent 12%))",
            borderRadius: 18,
            padding: 12,
            display: "grid",
            gap: 12,
          }}
        >
          <div style={{ display: "flex", alignItems: "flex-start", justifyContent: "space-between", gap: 12, flexWrap: "wrap" }}>
            <div>
              <div className="dash-eyebrow">Validation & Evidence</div>
              <div style={{ fontWeight: 950, fontSize: 15, marginTop: 2 }}>Remediation Assurance</div>
              <div style={{ color: "var(--muted)", fontSize: 12, lineHeight: 1.45, marginTop: 3 }}>
                Audit-ready validation runs, manual overrides, failed checks, and evidence coverage across remediation tasks.
              </div>
            </div>
            <span style={chipStyle(safeNumber(analytics?.failed_validations_last_7_days, 0) > 0 ? "danger" : "ok")}>
              {safeNumber(analytics?.validations_last_7_days, 0)} validations · 7d
            </span>
          </div>

          <div className="hero-kpis" style={{ gridTemplateColumns: "repeat(5, minmax(0, 1fr))" }}>
            {[
              "validation_runs",
              "failed_runs",
              "manual_overrides",
              "evidence_linked",
              "evidence_coverage",
            ].map((metricKey) => (
              <AssuranceMetricButton
                key={metricKey}
                metricKey={metricKey}
                analytics={analytics}
                evidenceCoveragePercent={evidenceCoveragePercent}
                onOpen={openAssuranceMetric}
              />
            ))}
          </div>
        </div>

        <div
          className="remediation-operations-panel__quick-views"
          style={{
            border: "1px solid color-mix(in srgb, var(--text), transparent 88%)",
            background: "color-mix(in srgb, var(--panel-2), transparent 22%)",
            borderRadius: 16,
            padding: 12,
            display: "grid",
            gap: 10,
          }}
        >
          <div style={{ display: "flex", alignItems: "center", justifyContent: "space-between", gap: 12, flexWrap: "wrap" }}>
            <div>
              <div style={{ fontWeight: 900, fontSize: 13 }}>Quick Views</div>
              <div style={{ color: "var(--muted)", fontSize: 12, marginTop: 2 }}>
                Jump directly into common remediation work queues.
              </div>
            </div>
            <button
              className="btn btn-ghost btn-xs"
              type="button"
              onClick={clearOperationsFilters}
              disabled={!filtersActive && activeTab === "all"}
            >
              Clear View
            </button>
          </div>

          <div className="filter-chips" style={{ gap: 8 }}>
            {quickViews.map((view) => {
              const active = quickView === view.key;
              return (
                <button
                  key={view.key}
                  type="button"
                  className={`chip ${active ? "active" : ""}`}
                  onClick={() => applyQuickView(view.key)}
                  disabled={view.disabled}
                  title={view.description}
                >
                  {view.label}
                </button>
              );
            })}
          </div>
        </div>

        <div
          className="remediation-operations-panel__filters"
          style={{
            border: "1px solid color-mix(in srgb, var(--text), transparent 88%)",
            background: "color-mix(in srgb, var(--panel-2), transparent 18%)",
            borderRadius: 16,
            padding: 12,
            display: "grid",
            gap: 10,
          }}
        >
          <div style={{ display: "flex", alignItems: "center", justifyContent: "space-between", gap: 12, flexWrap: "wrap" }}>
            <div>
              <div style={{ fontWeight: 900, fontSize: 13 }}>Operations Filters</div>
              <div style={{ color: "var(--muted)", fontSize: 12, marginTop: 2 }}>
                Search and narrow the full remediation queue before taking action.
              </div>
            </div>
            <button
              className="btn btn-ghost btn-xs"
              type="button"
              onClick={clearOperationsFilters}
              disabled={!filtersActive}
            >
              Clear Filters
            </button>
          </div>

          <div
            style={{
              display: "grid",
              gridTemplateColumns: "minmax(260px, 1.5fr) minmax(160px, .85fr) 110px minmax(130px, .75fr)",
              gap: 10,
              alignItems: "end",
            }}
          >
            <label style={labelStyle()}>
              Search
              <input
                style={inputStyle()}
                value={operationsSearch}
                onChange={(event) => setOperationsSearch(event.target.value)}
                placeholder="rule, control, resource, owner"
              />
            </label>

            <label style={labelStyle()}>
              Owner
              <OasysSelect
                value={ownerFilter}
                onChange={setOwnerFilter}
                placeholder="All owners"
                options={[
                  { value: "", label: "All owners" },
                  ...ownerFilterOptions.map((owner) => ({ value: owner.value, label: owner.label })),
                ]}
              />
            </label>

            <label style={labelStyle()}>
              Priority
              <OasysSelect
                value={priorityFilter}
                onChange={setPriorityFilter}
                placeholder="All"
                options={[
                  { value: "", label: "All" },
                  ...priorityFilterOptions.map((priority) => ({
                    value: priority.value,
                    label: priority.label,
                  })),
                ]}
              />
            </label>

            <label style={labelStyle()}>
              SLA
              <OasysSelect
                value={slaFilter}
                onChange={setSlaFilter}
                placeholder="All SLA states"
                options={[
                  { value: "", label: "All SLA states" },
                  ...slaFilterOptions.map((slaState) => ({
                    value: slaState.value,
                    label: slaState.label,
                  })),
                ]}
              />
            </label>
          </div>

          {activeFilterChips.length > 0 ? (
            <div
              style={{
                display: "flex",
                flexWrap: "wrap",
                gap: 8,
                alignItems: "center",
              }}
            >
              {activeFilterChips.map((chip) => (
                <button
                  key={chip.key}
                  type="button"
                  onClick={() => clearSingleOperationsFilter(chip.key)}
                  style={{
                    ...chipStyle("accent"),
                    borderRadius: 999,
                    cursor: "pointer",
                    gap: 7,
                  }}
                  title={`Clear ${chip.label}`}
                >
                  <span>{chip.label}</span>
                  <span
                    aria-hidden="true"
                    style={{
                      color: "var(--text)",
                      fontWeight: 950,
                      opacity: 0.86,
                    }}
                  >
                    ×
                  </span>
                </button>
              ))}
            </div>
          ) : null}

          <div style={{ color: "var(--muted)", fontSize: 12 }}>
            {filtersActive ? (
              <>
                Showing {tasks.length} of {queueTotal} matching task{queueTotal === 1 ? "" : "s"}.
              </>
            ) : (
              <>
                Showing {tasks.length} of {queueTotal} task{queueTotal === 1 ? "" : "s"} in this queue{isClosedQueue ? ", sorted by most recently closed" : ""}.
              </>
            )}
          </div>
        </div>

        <div
          style={{
            position: "relative",
            zIndex: 20,
            border: "1px solid color-mix(in srgb, var(--accent), transparent 70%)",
            background: "color-mix(in srgb, var(--accent), transparent 94%)",
            borderRadius: 16,
            padding: 12,
            display: "grid",
            gap: 12,
          }}
        >
          <div style={{ display: "flex", alignItems: "center", justifyContent: "space-between", gap: 12 }}>
            <div>
              <div style={{ fontWeight: 900, fontSize: 13 }}>Bulk Actions</div>
              <div style={{ color: "var(--muted)", fontSize: 12, marginTop: 2 }}>
                {selectedCount > 0
                  ? `${selectedCount} task${selectedCount === 1 ? "" : "s"} selected.`
                  : "Select tasks below to assign, reprioritize, set due dates, or update status."}
              </div>
            </div>
            <div style={{ display: "flex", gap: 8, flexWrap: "wrap", justifyContent: "flex-end" }}>
              <button className="btn btn-ghost btn-xs" type="button" onClick={toggleSelectAllVisible} disabled={visibleTaskIds.length === 0}>
                {allVisibleSelected ? "Clear Visible" : "Select Visible"}
              </button>
              <button className="btn btn-ghost btn-xs" type="button" onClick={clearSelection} disabled={selectedCount === 0 && !bulkNotice && !bulkError && !bulkNote}>
                Clear Selection
              </button>
            </div>
          </div>

          {bulkNotice ? (
            <div style={{ ...chipStyle("ok"), justifySelf: "start", borderRadius: 12 }}>{bulkNotice}</div>
          ) : null}
          {bulkError ? (
            <div style={{ ...chipStyle("danger"), justifySelf: "start", borderRadius: 12 }}>{bulkError}</div>
          ) : null}

          <div
            style={{
              display: "grid",
              gridTemplateColumns: "1fr 1fr 105px 150px",
              gap: 10,
              alignItems: "end",
            }}
          >
            <label style={labelStyle()}>
              Owner User ID
              <input
                style={inputStyle()}
                value={bulkOwnerUserId}
                onChange={(event) => setBulkOwnerUserId(event.target.value)}
                placeholder="user id or email"
              />
            </label>
            <label style={labelStyle()}>
              Owner Name
              <input
                style={inputStyle()}
                value={bulkOwnerName}
                onChange={(event) => setBulkOwnerName(event.target.value)}
                placeholder="owner display name"
              />
            </label>
            <label style={labelStyle()}>
              Priority
              <input
                style={inputStyle()}
                type="number"
                min="1"
                max="999"
                value={bulkPriority}
                onChange={(event) => setBulkPriority(event.target.value)}
                placeholder="50"
              />
            </label>
            <label style={labelStyle()}>
              Due Date
              <input
                style={inputStyle()}
                type="date"
                value={bulkDueDate}
                onChange={(event) => setBulkDueDate(event.target.value)}
              />
            </label>
          </div>

          <div
            style={{
              display: "grid",
              gridTemplateColumns: "180px minmax(220px, 1fr) auto auto",
              gap: 10,
              alignItems: "end",
            }}
          >
            <label style={labelStyle()}>
              Status
              <StatusDropdown
                value={bulkStatus}
                onChange={setBulkStatus}
                disabled={bulkBusy}
              />
            </label>
            <label style={labelStyle()}>
              Note
              <input
                style={inputStyle()}
                value={bulkNote}
                onChange={(event) => setBulkNote(event.target.value)}
                placeholder="optional audit note"
              />
            </label>
            <button
              className="btn btn-primary btn-sm"
              type="button"
              onClick={handleBulkAssign}
              disabled={bulkBusy || selectedCount === 0}
            >
              {bulkBusy ? "Applying…" : "Apply Assignment"}
            </button>
            <button
              className="btn btn-ghost btn-sm"
              type="button"
              onClick={handleBulkStatus}
              disabled={bulkBusy || selectedCount === 0}
            >
              {bulkBusy ? "Applying…" : "Update Status"}
            </button>
          </div>
        </div>

        <div
          className="remediation-operations-panel__table-shell"
          style={{
            border: "1px solid color-mix(in srgb, var(--text), transparent 88%)",
            borderRadius: 16,
            overflow: "hidden",
            background: "var(--panel)",
          }}
        >
          <div
            className="remediation-operations-panel__table-header"
          >
            <div>
              <input
                type="checkbox"
                checked={allVisibleSelected}
                onChange={toggleSelectAllVisible}
                disabled={visibleTaskIds.length === 0}
                aria-label="Select all visible remediation tasks"
              />
            </div>
            <div>Task</div>
            <div>State</div>
            <div>Owner</div>
            <div>{isClosedQueue ? "Closure" : "Timing"}</div>
            <div>Actions</div>
          </div>


          <div
            ref={taskListRef}
            className="remediation-operations-panel__table-viewport"
            style={{
              maxHeight: "560px",
              overflowY: "auto",
              overflowX: "hidden",
              scrollbarGutter: "stable",
            }}
          >
          {loading && tasks.length === 0 ? (
            <div style={{ padding: 16, color: "var(--muted)", fontSize: 13 }}>
              Loading remediation operations…
            </div>
          ) : null}

          {!loading && tasks.length === 0 ? (
            <div style={{ padding: 16, color: "var(--muted)", fontSize: 13 }}>
              No remediation tasks match this operations queue.
            </div>
          ) : null}

          {displayedTasks.map((task) => {
            const tone = statusTone(task.status, task.validation_status);
            const validationTone = statusTone("", task.validation_status);
            const deadline = isClosedQueue ? getClosedDateLabels(task) : getDeadlineLabels(task);
            const slaTone = getSlaTone(task);
            const selected = selectedTaskIds.has(task.id);

            return (
              <div
                key={task.id}
                className={`remediation-operations-panel__task-row ${selected ? "is-selected" : ""}`}
              >
                <div>
                  <input
                    type="checkbox"
                    checked={selected}
                    onChange={() => toggleTaskSelected(task.id)}
                    aria-label={`Select ${taskLabel(task)}`}
                  />
                </div>

                <div style={{ display: "grid", gap: 4, minWidth: 0 }}>
                  <div
                    style={{
                      fontWeight: 850,
                      fontSize: 13,
                      lineHeight: 1.35,
                      overflowWrap: "anywhere",
                    }}
                  >
                    {taskLabel(task)}
                  </div>
                  <div
                    style={{
                      color: "var(--muted)",
                      fontSize: 12,
                      lineHeight: 1.35,
                      overflow: "hidden",
                      textOverflow: "ellipsis",
                      whiteSpace: "nowrap",
                    }}
                    title={task.resource_id || ""}
                  >
                    {cleanValue(task.resource_id, "No resource scoped")}
                  </div>
                </div>

                <div style={{ display: "flex", gap: 6, flexWrap: "wrap", alignItems: "center" }}>
                  <span style={chipStyle(tone)}>{getTaskStatusLabel(task.status)}</span>
                  <span style={chipStyle(validationTone)}>
                    {formatValidationLabel(task.validation_status)}
                  </span>
                </div>

                <div style={{ minWidth: 0 }}>
                  <div
                    style={{
                      fontWeight: 800,
                      fontSize: 12,
                      overflow: "hidden",
                      textOverflow: "ellipsis",
                      whiteSpace: "nowrap",
                    }}
                    title={task.owner_name || task.owner_user_id || "Unassigned"}
                  >
                    {cleanValue(task.owner_name || task.owner_user_id, "Unassigned")}
                  </div>
                  <div style={{ color: "var(--muted)", fontSize: 11, marginTop: 3 }}>
                    P{safeNumber(task.priority, 100)} · age {safeNumber(task.aging_days, 0)}d
                  </div>
                </div>

                <div style={{ display: "grid", gap: 5 }}>
                  <div>
                    <div
                      style={{
                        fontWeight: 800,
                        fontSize: 12,
                        color:
                          deadline.tone === "danger"
                            ? "var(--danger)"
                            : deadline.tone === "warn"
                            ? "var(--warn)"
                            : "var(--text)",
                      }}
                    >
                      {deadline.dateLabel}
                    </div>
                    <div
                      style={{
                        color:
                          deadline.tone === "danger"
                            ? "var(--danger)"
                            : deadline.tone === "warn"
                            ? "var(--warn)"
                            : "var(--muted)",
                        fontSize: 11,
                        marginTop: 2,
                      }}
                    >
                      {deadline.helperLabel}
                    </div>
                  </div>

                  <div style={{ display: "flex", gap: 6, alignItems: "center", flexWrap: "wrap" }}>
                    <span style={chipStyle(isClosedQueue ? "ok" : slaTone)}>
                      {isClosedQueue ? "Closed" : formatSlaStateLabel(task.sla_state)}
                    </span>
                    <span style={{ color: "var(--muted)", fontSize: 11 }}>
                      {isClosedQueue
                        ? isApprovedExceptionTask(task)
                          ? "Approved exception"
                          : "Audit preserved"
                        : formatSlaWindowLabel(task)}
                    </span>
                  </div>
                </div>

                <div
                  className="remediation-operations-panel__task-actions"
                  style={{
                    display: "grid",
                    gridTemplateColumns: "minmax(0, 1fr)",
                    gap: 7,
                    alignItems: "center",
                  }}
                >
                  {(() => {
                    const action = getPrimaryWorkflowAction(task);
                    if (!action) return null;

                    if (action.key === "fix_plan") {
                      return typeof onOpenFixPlan === "function" ? (
                        <button
                          className="btn btn-ghost btn-xs"
                          type="button"
                          style={{ width: "100%", whiteSpace: "nowrap" }}
                          onClick={() => onOpenFixPlan(task)}
                        >
                          {action.label}
                        </button>
                      ) : null;
                    }

                    return (
                      <button
                        className={`btn ${action.tone === "primary" ? "btn-primary" : "btn-ghost"} btn-xs`}
                        type="button"
                        style={{ width: "100%", whiteSpace: "nowrap", fontSize: 11 }}
                        onClick={() => handleWorkflowAction(task, action.key)}
                        disabled={
                          workflowBusyTaskId === task.id ||
                          (action.key === "run_validation" && isValidationRunning)
                        }
                      >
                        {workflowBusyTaskId === task.id ? "Working…" : action.label}
                      </button>
                    );
                  })()}

                  {typeof onOpenTask === "function" ? (
                    <button
                      className="btn btn-ghost btn-xs"
                      type="button"
                      style={{ width: "100%", whiteSpace: "nowrap" }}
                      onClick={() => onOpenTask(task)}
                    >
                      Review
                    </button>
                  ) : null}

                  {typeof onOpenFixPlan === "function" &&
                  normalizeText(task.status) !== "closed" &&
                  normalizeText(task.validation_status) !== "failed" ? (
                    <button
                      className="btn btn-ghost btn-xs"
                      type="button"
                      style={{ width: "100%", whiteSpace: "nowrap" }}
                      onClick={() => onOpenFixPlan(task)}
                    >
                      Fix Plan
                    </button>
                  ) : null}
                </div>
              </div>
            );
          })}
          </div>
        </div>

        <div
          className="remediation-operations-panel__footer"
          style={{
            display: "flex",
            alignItems: "center",
            justifyContent: "space-between",
            gap: 12,
            flexWrap: "wrap",
            color: "var(--muted)",
            fontSize: 12,
            lineHeight: 1.45,
          }}
        >
          <div>
            {filtersActive ? (
              <>
                Showing {safeNumber(tasks.length, queueReturned)} of {queueTotal} matching task{queueTotal === 1 ? "" : "s"}.
              </>
            ) : (
              <>
                Showing {safeNumber(tasks.length, queueReturned)} of {queueTotal} {isClosedQueue ? "closed" : "active"} remediation task
                {queueTotal === 1 ? "" : "s"}.
              </>
            )}{" "}
            Last loaded {formatTimestamp(queuePayload?.at || analytics?.at)}.
          </div>

          {hasMoreTasks ? (
            <Button
              variant="secondary"
              size="small"
              onClick={handleLoadMore}
              loading={loadingMore}
              loadingLabel="Loading more…"
              disabled={loading}
            >
              {`Load More (${queueTotal - tasks.length} remaining)`}
            </Button>
          ) : null}
        </div>
      </div>

      <AssuranceMetricModal
        metricKey={selectedAssuranceMetric}
        analytics={analytics}
        evidenceCoveragePercent={evidenceCoveragePercent}
        activities={latestRemediationActivity}
        onClose={closeAssuranceMetric}
        onOpenTask={onOpenTask}
      />
    </section>
  );
}